import { useState } from 'react';
import { DebtSettlementKind } from '@signalvote/shared';
import type { DebtDto } from '@signalvote/shared';
import { useSettleDebt } from '@/hooks/queries';

// Recording that a creditor took less than the full amount — or let it go entirely — is its own
// act, not an edit of the debt, so it gets its own form. The incurred amount is deliberately NOT
// on it: a settlement records how an obligation ENDED, never what it was (52 U.S.C. 30104(b)(8)
// wants both disclosed). The reason is required for the same statutory reason, and the server
// enforces it too.

const money = (n: number) => n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });

interface SettleDebtFormProps {
  debt: DebtDto;
  /** Settlement recorded — the row goes back to its read view (the list refetches itself). */
  onDone: () => void;
  onCancel: () => void;
}

export function SettleDebtForm({ debt, onDone, onCancel }: SettleDebtFormProps) {
  const settleDebt = useSettleDebt();

  const [kind, setKind] = useState<DebtSettlementKind>(DebtSettlementKind.Settled);
  const [settlementDate, setSettlementDate] = useState('');
  // The settled amount IS the cash the creditor received, and the server holds it to the payments
  // already recorded against this debt — so prefill it rather than asking twice. This form mounts
  // when the treasurer opens it, so the figure is read fresh rather than behind a stale row.
  const [settlementAmount, setSettlementAmount] = useState(String(debt.paidToDate));
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSettle = async () => {
    setError(null);
    try {
      await settleDebt.mutateAsync({
        id: debt.id,
        data: {
          kind,
          settlementDate,
          settlementAmount: Number(settlementAmount),
          reason: reason.trim(),
        },
      });
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to record the settlement');
    }
  };

  return (
    <div className="rounded-md border p-3">
      <p className="text-sm font-medium">
        Settle or forgive {money(debt.incurredAmount)} owed to {debt.contactName ?? 'the creditor'}
      </p>
      <p className="mt-1 text-xs text-gray-500">
        The amount owed stays on the record — your report still discloses it. Only the balance
        clears, from the date you give here.
      </p>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <label className="text-xs">
          <span className="mb-1 block font-medium">What happened</span>
          <select
            aria-label="What happened"
            value={kind}
            onChange={(e) => setKind(e.target.value as DebtSettlementKind)}
            className="w-full rounded-md border px-2 py-1 text-sm"
          >
            <option value={DebtSettlementKind.Settled}>The creditor accepted less</option>
            <option value={DebtSettlementKind.Forgiven}>The creditor forgave it</option>
          </select>
        </label>
        <label className="text-xs">
          <span className="mb-1 block font-medium">Date extinguished</span>
          <input
            aria-label="Date extinguished"
            type="date"
            value={settlementDate}
            onChange={(e) => setSettlementDate(e.target.value)}
            className="w-full rounded-md border px-2 py-1 text-sm"
          />
        </label>
        <label className="text-xs">
          <span className="mb-1 block font-medium">Amount the creditor accepted</span>
          <input
            aria-label="Amount the creditor accepted"
            type="number"
            min={0}
            step={0.01}
            value={settlementAmount}
            onChange={(e) => setSettlementAmount(e.target.value)}
            className="w-full rounded-md border px-2 py-1 text-sm"
          />
          <span className="mt-1 block text-gray-500">
            What has actually been paid on this debt — record the payment as an expenditure first
            if you haven&apos;t.
          </span>
        </label>
        <label className="text-xs sm:col-span-2">
          <span className="mb-1 block font-medium">Why it was extinguished</span>
          <textarea
            aria-label="Why it was extinguished"
            rows={2}
            maxLength={1000}
            placeholder="e.g. Vendor accepted 60% in full satisfaction as the campaign wound down"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full rounded-md border px-2 py-1 text-sm"
          />
        </label>
      </div>
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          disabled={settleDebt.isPending || settlementDate === '' || reason.trim() === ''}
          onClick={handleSettle}
          className="rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
        >
          {settleDebt.isPending ? 'Saving…' : 'Record settlement'}
        </button>
        <button type="button" onClick={onCancel} className="rounded-md border px-3 py-1 text-xs">
          Cancel
        </button>
      </div>
    </div>
  );
}
